import React from 'react';
import { motion } from 'framer-motion';
import { Shield, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const lastUpdated = "February 23, 2025";

  return (
    <motion.div 
      className="min-h-screen bg-black relative py-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0iIzAwMCIvPjxwYXRoIGQ9Ik0zMCAzMG0tMiAwYTIgMiAwIDEgMSA0IDBhMiAyIDAgMSAxLTQgMCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==')]"></div>
      
      <div className="container mx-auto px-4 max-w-4xl relative z-10">
        <Button 
          variant="ghost" 
          className="mb-6 text-amber-500 hover:text-amber-400 hover:bg-amber-900/20" 
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="bg-gray-900/90 border-2 border-amber-900/30 rounded-lg p-8">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8 text-amber-500" />
            <h1 className="text-3xl font-headline text-amber-500 tracking-wider">PRIVACY POLICY</h1>
          </div>
          
          <p className="text-gray-400 font-typewriter text-sm mb-8">Last Updated: {lastUpdated}</p>

          <div className="space-y-6 text-gray-300 font-typewriter">
            <section>
              <h2 className="text-xl font-headline text-amber-400 tracking-wider mb-3">1. INTRODUCTION</h2>
              <p className="text-sm leading-relaxed">
                Deadline by Daria ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, and safeguard your information when you use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-headline text-amber-400 tracking-wider mb-3">2. INFORMATION WE COLLECT</h2>
              <p className="text-sm leading-relaxed mb-2">We collect the following types of information:</p>
              <ul className="list-disc list-inside text-sm space-y-2 ml-4">
                <li><strong>Account Information:</strong> Email address, display name, and password when you register</li>
                <li><strong>Payment Information:</strong> Payment details processed securely through Stripe (we do not store full card numbers)</li>
                <li><strong>Usage Data:</strong> Information about how you interact with our Service, including pages viewed and features used</li>
                <li><strong>Device Information:</strong> Browser type, device type, and IP address for security and analytics purposes</li>
                <li><strong>Communications:</strong> Messages sent through our direct messaging and chat features</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-headline text-amber-400 tracking-wider mb-3">3. HOW WE USE YOUR INFORMATION</h2>
              <p className="text-sm leading-relaxed mb-2">We use your information to:</p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-4">
                <li>Provide and maintain the Service</li>
                <li>Process subscriptions and payments</li>
                <li>Send push notifications (with your consent)</li>
                <li>Communicate with you about your account and updates</li>
                <li>Improve and personalize your experience</li>
                <li>Ensure security and prevent fraud</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-headline text-amber-400 tracking-wider mb-3">4. DATA SHARING</h2>
              <p className="text-sm leading-relaxed mb-2">We may share your information with:</p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-4">
                <li><strong>Payment Processors:</strong> Stripe for secure payment processing</li>
                <li><strong>Cloud Services:</strong> For data storage and hosting</li>
                <li><strong>Analytics Providers:</strong> To understand usage patterns (anonymized data)</li>
                <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
              </ul>
              <p className="text-sm leading-relaxed mt-2">
                We do not sell your personal information to third parties.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-headline text-amber-400 tracking-wider mb-3">5. DATA SECURITY</h2>
              <p className="text-sm leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information, 
                including encryption, secure servers, and access controls. However, no method of transmission over 
                the Internet is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-headline text-amber-400 tracking-wider mb-3">6. COOKIES & TRACKING</h2>
              <p className="text-sm leading-relaxed">
                We use cookies and similar technologies to maintain your session, remember your preferences, and 
                analyze usage. You can control cookies through your browser settings, but some features may not 
                function properly if cookies are disabled.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-headline text-amber-400 tracking-wider mb-3">7. YOUR RIGHTS</h2>
              <p className="text-sm leading-relaxed mb-2">You have the right to:</p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-4">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your account and data</li>
                <li>Opt out of marketing communications</li>
                <li>Withdraw consent for push notifications</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-headline text-amber-400 tracking-wider mb-3">8. DATA RETENTION</h2>
              <p className="text-sm leading-relaxed">
                We retain your information for as long as your account is active or as needed to provide services. 
                We may retain certain information as required by law or for legitimate business purposes after 
                account deletion.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-headline text-amber-400 tracking-wider mb-3">9. CHILDREN'S PRIVACY</h2>
              <p className="text-sm leading-relaxed">
                The Service is not intended for users under 18 years of age. We do not knowingly collect information 
                from children under 18. If we learn we have collected such information, we will delete it promptly.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-headline text-amber-400 tracking-wider mb-3">10. CHANGES TO THIS POLICY</h2>
              <p className="text-sm leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by 
                posting the new policy on this page and updating the "Last Updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-headline text-amber-400 tracking-wider mb-3">11. CONTACT US</h2>
              <p className="text-sm leading-relaxed">
                If you have questions about this Privacy Policy or wish to exercise your rights, please contact us 
                through the messaging feature on the platform.
              </p>
            </section>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PrivacyPolicy;
